import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { apiService } from '../services/apiService';

export default function TalentTransferModal({ isOpen, onClose, talent, onTransferSuccess }) {
    const { t } = useTranslation();
    const [salesUsers, setSalesUsers] = useState([]);
    const [selectedSalesId, setSelectedSalesId] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState('');

    useEffect(() => {
        if (isOpen) {
            fetchSalesUsers();
            setSelectedSalesId('');
            setError('');
        }
    }, [isOpen]); // eslint-disable-line react-hooks/exhaustive-deps

    const fetchSalesUsers = async () => {
        try {
            setIsLoading(true);
            // 这里假设有一个获取销售员列表的API
            const users = await apiService.getSalesUsers();
            // 保存所有用户用于显示当前负责人
            setSalesUsers(users);
        } catch (error) {
            console.error('获取销售员列表失败:', error);
            setError(t('loadSalesUsersFailed'));
        } finally {
            setIsLoading(false);
        }
    };

    const handleTransfer = async () => {
        if (!selectedSalesId) {
            setError(t('pleaseSelectSales'));
            return;
        }

        try {
            setIsLoading(true);
            setError('');
            
            await apiService.transferTalent(talent.id, selectedSalesId);
            onTransferSuccess();
            onClose();
        } catch (error) {
            console.error('人才转让失败:', error);
            setError(error.message || t('transferTalentFailed'));
        } finally {
            setIsLoading(false);
        }
    };

    if (!isOpen) return null;

    const selectedSalesUser = salesUsers.find(user => user.id === selectedSalesId);
    const currentSalesUser = salesUsers.find(user => user.id === talent?.salesStaffId) || 
                            { username: talent?.salesStaff?.username || '未知用户' };

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex justify-center items-center p-4">
            <div className="bg-white rounded-lg shadow-xl w-full max-w-md">
                <div className="p-6 border-b">
                    <h2 className="text-2xl font-bold text-gray-800">{t('transferTalent')}</h2>
                </div>
                
                <div className="p-6">
                    {error && (
                        <div className="mb-4 p-3 bg-red-100 border border-red-400 text-red-700 rounded">
                            {error}
                        </div>
                    )}
                    
                    {/* 人才信息 */}
                    <div className="mb-6 p-4 bg-gray-50 rounded-lg">
                        <h3 className="font-semibold text-gray-700 mb-2">{t('talentInfo')}</h3>
                        <p className="text-sm"><span className="font-medium">{t('name')}:</span> {talent?.fullName}</p>
                        <p className="text-sm"><span className="font-medium">{t('managementNumber')}:</span> {talent?.managementNumber}</p>
                        <p className="text-sm"><span className="font-medium">{t('currentSales')}:</span> {currentSalesUser.username}</p>
                    </div>

                    {/* 转让目标选择 */}
                    <div className="mb-6">
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                            {t('transferToSales')}
                        </label>
                        <select
                            value={selectedSalesId}
                            onChange={(e) => setSelectedSalesId(e.target.value)}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                            disabled={isLoading}
                        >
                            <option value="">{t('pleaseSelect')}</option>
                            {salesUsers
                                .filter(user => user.id !== talent?.salesStaffId)
                                .map(user => (
                                <option key={user.id} value={user.id}>
                                    {user.username}
                                </option>
                            ))}
                        </select>
                    </div>

                    {/* 确认信息 */}
                    {selectedSalesUser && (
                        <div className="mb-6 p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
                            <h4 className="font-medium text-yellow-800 mb-2">{t('transferConfirmation')}</h4>
                            <p className="text-sm text-yellow-700">
                                {talent?.fullName} を {currentSalesUser.username} から {selectedSalesUser.username} に譲渡します。よろしいですか？
                            </p>
                        </div>
                    )}
                </div>

                <div className="p-6 border-t flex justify-end space-x-3">
                    <button
                        type="button"
                        onClick={onClose}
                        className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 border border-gray-300 rounded-md hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500"
                        disabled={isLoading}
                    >
                        {t('cancel')}
                    </button>
                    <button
                        type="button"
                        onClick={handleTransfer}
                        className="px-4 py-2 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:bg-blue-400"
                        disabled={isLoading || !selectedSalesId}
                    >
                        {isLoading ? t('transferring') : t('confirmTransfer')}
                    </button>
                </div>
            </div>
        </div>
    );
}